/**
 * AuthContext - Centralized authentication state management
 * 
 * SINGLE SOURCE OF TRUTH for auth state across entire app:
 * - Onboarding, Dashboard, Chitragupt, Session Chat, Profile
 * 
 * Features:
 * - Session persistence via localStorage token + cookie
 * - Automatic session restore on app load
 * - Debug logging for auth state tracking
 * - Protection against race conditions
 * 
 * FIXED: Race condition where initAuth would interfere with handleAuthCallback
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getBackendUrl } from '../config/api';
import { clientHeaders } from '../utils/clientHeaders';
import { AUTH_TOKEN_KEY, AUTH_USER_KEY, clearStoredAuth, performLogout } from '../utils/logout';

// Use runtime URL resolution for same-origin strategy
const getApiUrl = () => getBackendUrl();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context;
};

/**
 * Debug logger for auth state
 */
const authDebug = (message, data = {}) => {
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  console.log(`[AUTH ${timestamp}] ${message}`, {
    ...data,
    sessionExists: !!localStorage.getItem(AUTH_TOKEN_KEY),
    userId: data.user?.id || data.user?.user_id || 'none'
  });
};

export const AuthProvider = ({ children }) => {
  // Core auth state - initialize from localStorage synchronously to prevent flash
  const [user, setUserState] = useState(() => {
    try {
      const cached = localStorage.getItem(AUTH_USER_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        authDebug('Initial user from localStorage', { user: parsed });
        return parsed;
      }
    } catch (e) {
      console.warn('[AUTH] Failed to parse cached user on init');
    }
    return null;
  });
  
  const [authLoading, setAuthLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [justAuthenticated, setJustAuthenticated] = useState(false);
  
  // Ref to track if we're in the middle of an auth operation
  const authOperationInProgress = useRef(false);
  // Ref to store the latest user for closure issues
  const userRef = useRef(user);
  // CRITICAL: Flag to prevent initAuth from interfering after handleAuthCallback
  const authCallbackInProgress = useRef(false);
  // Track if init has already run
  const initCompleted = useRef(false);
  
  // Keep userRef in sync
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  
  // Computed states
  const isAuthenticated = !!user;
  const hasCompleteProfile = !!(user?.onboarding_complete && user?.date && user?.time && user?.place);

  /**
   * Set user with persistence
   */
  const setUser = useCallback((userData) => {
    authDebug('Setting user', { user: userData });
    setUserState(userData);
    userRef.current = userData;
    
    if (userData) {
      // Persist user data for quick restore
      try {
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
      } catch (e) {
        console.warn('[AUTH] Failed to persist user data:', e);
      }
    } else {
      localStorage.removeItem(AUTH_USER_KEY);
    }
  }, []);

  /**
   * Get stored token
   */
  const getStoredToken = useCallback(() => {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }, []);

  /**
   * Store session token
   */
  const storeToken = useCallback((token) => {
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      authDebug('Token stored');
    }
  }, []);

  /**
   * Clear all auth data
   */
  const clearAuthData = useCallback(() => {
    clearStoredAuth();
    setUserState(null);
    userRef.current = null;
    authDebug('Auth data cleared');
  }, []);

  /**
   * Fetch current user from /api/auth/me
   * Core function for session validation
   * Returns the user data on success, null on failure
   * 
   * SILENT AUTH: On 400/401 errors during initial load, we silently clear
   * stale tokens without showing any UI error. This handles "zombie tokens"
   * gracefully - user just sees the login page.
   */
  const fetchCurrentUser = useCallback(async (options = {}) => {
    const { skipIfUserExists = false, forceRefresh = false, isInitialLoad = false } = options;
    const currentUser = userRef.current;
    
    // CRITICAL: Don't fetch if auth callback is in progress
    if (authCallbackInProgress.current) {
      authDebug('Auth callback in progress, skipping fetch');
      return currentUser;
    }
    
    // Skip if user exists and we're told to skip
    if (skipIfUserExists && currentUser && !forceRefresh) {
      authDebug('Skipping fetch - user already exists');
      return currentUser;
    }
    
    // Prevent concurrent auth operations
    if (authOperationInProgress.current && !forceRefresh) {
      authDebug('Auth operation already in progress, skipping');
      return currentUser;
    }
    
    authOperationInProgress.current = true;
    authDebug('Fetching current user from /api/auth/me', { isInitialLoad });
    
    try {
      const storedToken = getStoredToken();
      const headers = {
        'Content-Type': 'application/json',
        ...clientHeaders(),
      };

      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const response = await fetch(`${getApiUrl()}/api/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });
      
      if (response.ok) {
        const userData = await response.json();
        authDebug('User fetched successfully from server', { user: userData });
        // Update state and ref together
        setUserState(userData);
        userRef.current = userData;
        // Also persist to localStorage
        try {
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
        } catch (e) {
          console.warn('[AUTH] Failed to persist user:', e);
        }
        authOperationInProgress.current = false;
        return userData;
      } else {
        authDebug('Auth check failed', { status: response.status, isInitialLoad });
        
        // SILENT AUTH CLEANUP: On 400/401/403 during ANY load with a token,
        // silently clear stale auth data without showing any UI error.
        // This handles "zombie tokens" gracefully.
        if ((response.status === 400 || response.status === 401 || response.status === 403) && 
            !authCallbackInProgress.current) {
          authDebug('Stale token detected - silently clearing auth data');
          // Silently clear all auth data - no UI error needed
          clearAuthData();
        }
        
        authOperationInProgress.current = false;
        return null;
      }
    } catch (err) {
      authDebug('Auth check error', { error: err.message });
      // Network errors - don't clear auth, might be temporary
      authOperationInProgress.current = false;
      return null;
    }
  }, [getStoredToken, clearAuthData]);

  /**
   * Handle auth callback after Google sign-in
   * Called by AuthCallback component AND AppRouter
   * CRITICAL: This is the main entry point after Google Auth completes
   */
  const handleAuthCallback = useCallback(async (sessionToken, userData) => {
    authDebug('handleAuthCallback called', { 
      hasToken: !!sessionToken, 
      user: userData 
    });
    
    // Set flag to prevent initAuth from interfering
    authCallbackInProgress.current = true;
    authOperationInProgress.current = true;
    
    // Store the session token FIRST
    if (sessionToken) {
      storeToken(sessionToken);
    }
    
    // Set user immediately and persist
    if (userData) {
      setUserState(userData);
      userRef.current = userData;
      try {
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
      } catch (e) {
        console.warn('[AUTH] Failed to persist user:', e);
      }
      setJustAuthenticated(true);
      
      // Clear justAuthenticated after a short delay
      setTimeout(() => setJustAuthenticated(false), 3000);
    }
    
    // Mark auth as complete
    setAuthLoading(false);
    setAuthChecked(true);
    
    authOperationInProgress.current = false;
    // Keep authCallbackInProgress true for a bit to prevent race conditions
    setTimeout(() => {
      authCallbackInProgress.current = false;
      authDebug('Auth callback protection window closed');
    }, 2000);
    
    authDebug('handleAuthCallback complete - user is now authenticated', { 
      user: userData,
      isAuthenticated: !!userData
    });
    return userData;
  }, [storeToken]);

  /**
   * Start Google sign-in flow
   */
  const startGoogleAuth = useCallback((featureName = 'app') => {
    authDebug('Starting Google auth', { feature: featureName });
    
    // Store return info
    sessionStorage.setItem('authReturnPath', window.location.pathname);
    sessionStorage.setItem('authReturnView', 'dashboard');
    sessionStorage.setItem('authFeature', featureName);
    
    // Redirect to login page with Google One Tap
    window.location.href = '/login';
  }, []);

  /**
   * Logout - clear all auth state
   */
  const logout = useCallback(async () => {
    authDebug('Logging out');

    // React state first, then performLogout clears storage synchronously before
    // it touches the network. Callers redirect the moment this resolves, so any
    // clearing sequenced after the await would be racing the page teardown.
    setUserState(null);
    userRef.current = null;

    await performLogout({ apiUrl: getApiUrl() });

    authDebug('Auth data cleared');
  }, []);

  /**
   * Check if session is valid (quick check without server call)
   */
  const hasValidSession = useCallback(() => {
    const token = getStoredToken();
    const currentUser = userRef.current;
    const hasToken = !!token;
    const hasUser = !!currentUser;
    
    authDebug('Session validity check', { 
      hasToken, 
      hasUser, 
      isValid: hasToken && hasUser 
    });
    
    return hasToken && hasUser;
  }, [getStoredToken]);

  /**
   * Initial session restore on app load
   * FIXED: Now respects authCallbackInProgress flag
   * FIXED: Silently handles stale tokens without showing errors
   */
  useEffect(() => {
    // Skip if already completed or callback in progress
    if (initCompleted.current) {
      authDebug('Init already completed, skipping');
      return;
    }
    
    let mounted = true;
    
    const initAuth = async () => {
      // CRITICAL: If auth callback is in progress, don't interfere
      if (authCallbackInProgress.current) {
        authDebug('Auth callback in progress, skipping init');
        return;
      }
      
      authDebug('=== AUTH INITIALIZATION START ===');

      // MAGIC-LINK INGESTION: an authenticated deep link (/r/<token>) redirects
      // here with the session token in the URL fragment (#jp_auth=<token>). The
      // SPA authenticates via the localStorage token, so adopt it, then strip
      // the fragment so the token never lingers in history/address bar. The
      // token is then validated by fetchCurrentUser below, same as any login.
      try {
        const hash = window.location.hash || '';
        const m = hash.match(/[#&]jp_auth=([^&]+)/);
        if (m && m[1]) {
          const magicToken = decodeURIComponent(m[1]);
          localStorage.setItem(AUTH_TOKEN_KEY, magicToken);
          authDebug('Adopted session token from magic-link fragment');
          const cleanedHash = hash.replace(/[#&]jp_auth=[^&]+/, '').replace(/^#?&?/, '');
          const newUrl = window.location.pathname + window.location.search +
            (cleanedHash ? '#' + cleanedHash : '');
          window.history.replaceState(null, '', newUrl);
        }
      } catch (e) {
        console.warn('[AUTH] magic-link fragment ingestion failed:', e);
      }

      // Check if we already have a user from localStorage (set in useState initializer)
      const hasInitialUser = !!userRef.current;
      authDebug('Initial state check', { hasInitialUser, user: userRef.current });
      
      // Check for token
      const token = getStoredToken();
      
      if (token) {
        authDebug('Token found, validating with server (silent mode)');
        // Validate with server - SILENT MODE: if validation fails,
        // clearAuthData is called internally without any UI error
        const serverUser = await fetchCurrentUser({ 
          skipIfUserExists: false, 
          forceRefresh: false,
          isInitialLoad: true  // Flag for silent error handling
        });
        
        if (mounted && !authCallbackInProgress.current) {
          if (serverUser) {
            authDebug('Server validation successful', { user: serverUser });
          } else {
            // Token was invalid - auth data already cleared silently by fetchCurrentUser
            authDebug('Server validation failed - stale token cleaned up silently');
          }
        }
      } else {
        authDebug('No token found, user is not authenticated');
        // Clear any stale cached user if no token
        if (userRef.current && !authCallbackInProgress.current) {
          clearAuthData();
        }
      }
      
      if (mounted && !authCallbackInProgress.current) {
        // IMPORTANT: Set loading false AFTER all async operations complete
        setAuthLoading(false);
        setAuthChecked(true);
        initCompleted.current = true;
        authDebug('=== AUTH INITIALIZATION COMPLETE ===', { 
          isAuthenticated: !!userRef.current,
          user: userRef.current 
        });
      }
    };
    
    initAuth();
    
    return () => { mounted = false; };
  }, []); // Only run on mount - eslint warning suppressed inline

  // Context value
  const value = {
    // State
    user,
    isAuthenticated,
    authLoading,
    authChecked,
    justAuthenticated,
    hasCompleteProfile,
    
    // Methods
    setUser,
    setJustAuthenticated,
    fetchCurrentUser,
    handleAuthCallback,
    startGoogleAuth,
    logout,
    hasValidSession,
    getStoredToken,
    
    // Debug helper
    debugAuthState: () => {
      const token = getStoredToken();
      console.log('=== AUTH DEBUG STATE ===');
      console.log('Session exists:', !!token);
      console.log('User ID:', user?.id || user?.user_id || 'none');
      console.log('User email:', user?.email || 'none');
      console.log('Is authenticated:', isAuthenticated);
      console.log('Auth loading:', authLoading);
      console.log('Auth checked:', authChecked);
      console.log('Callback in progress:', authCallbackInProgress.current);
      console.log('========================');
      return {
        sessionExists: !!token,
        userId: user?.id || user?.user_id,
        userEmail: user?.email,
        isAuthenticated,
        authLoading,
        authChecked
      };
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
